import React, { useMemo, useState, useEffect } from "react";
import { BiRightArrow } from "react-icons/bi";
import { GoArrowLeft } from "react-icons/go";
import { FiArrowRightCircle } from "react-icons/fi";
import { Navigate, useNavigate } from "react-router-dom";
import BotModal from "../components/UI/BotModal";
import CustomizedModal from "../components/UI/CustomizedModal";
import { useAuth } from "../context/AuthContext";
import { useUser } from "../context/UserContext";
import useReCaptcha from "../hooks/useReCaptcha";
import { sendOTPEmailToUser } from "../services/EmailServices";
import { validateUser } from "../services/LoginServices";
import { validateEmail } from "../utility/Utility";
import {
  ALL_ACCESS,
  VENDORS,
  WATER_SYSTEMS,
} from "../utility/routingInformation";

const COUNTDOWN_TIME = 60;

const SignIn = () => {
  const [showPassword, setShowPassword] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  const [modal, setModal] = useState();
  const [botModal, setBotModal] = useState();
  const navigate = useNavigate();

  const { isLoggedIn, onLogin, onLogout } = useAuth();
  const { updateUserData } = useUser();

  const [isDisabled, setIsDisabled] = useState(true);

  const [countdown, setCountdown] = useState(COUNTDOWN_TIME);

  const [startCountdown, setStartCountdown] = useState(false);

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);

  const [isSending, setIsSending] = useState(false);

  const [sendingPasscode, setSendingPasscode] = useState(false);

  const startOTPCountdown = () => {
    setStartCountdown(false);
    setIsDisabled(true);
    setCountdown(COUNTDOWN_TIME);
    setStartCountdown(prev=>true);
  };

  const stopOTPCountdown = () => {
    setStartCountdown(false);
    setIsDisabled(true);
    setCountdown(COUNTDOWN_TIME);
    setStartCountdown(false);
    setOtp(["", "", "", "", "", ""]);
    setIsSending(false);
    setIsLoading(false);
  };

  useEffect(() => {
    if (startCountdown && countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prevCountdown) => prevCountdown - 1);
      }, 1000);

      return () => clearInterval(timer);
    } else if (countdown === 0) {
      setIsDisabled(false); 
      setStartCountdown(false); 
    }
    else{
      setStartCountdown(prev=>true);
    }
  }, [startCountdown, countdown]);

  useMemo(() => {
    if ("email" in sessionStorage || "pfas_otp_signin_email" in localStorage) {
      //show password box since email has already been stored
      let stored_email = sessionStorage.getItem("email") || localStorage.getItem("pfas_otp_signin_email");
      setEmail(stored_email);
      // setEmail(sessionStorage.getItem("email"));
      setShowPassword(true);
    }
  }, [showPassword]);

  const { reCaptchaLoaded, generateReCaptchaToken } = useReCaptcha();

  if (isLoggedIn) return <Navigate to="/dashboard" replace />;

  const checkForEnterInEmail = (e) => {
    if (e.key === "Enter") {
      handleEmailInput(e);
      sendEmail(e);
    }
  };

  const checkForEnterInPassword = (e) => {
    if (e.key === "Enter") {
      handlePasswordInput(e);
      sendPassword(e);
    }
  };

  const backToEmail= (e)=>{
    e.preventDefault();
    sessionStorage.removeItem("email");
    localStorage.removeItem("pfas_otp_signin_email");
    setShowPassword(prev=>false);
    setEmail("");
    stopOTPCountdown();
  }

  const handleEmailInput = (e) => {
    setEmail(e.target.value.trim());
  };

  const handlePasswordInput = (e) => {
    if (isNaN(e.target.value)) return;
    setPassword(e.target.value.trim());
  };

  //Resend passcode handler
  const resendOTPHandler = async () => {
    setSendingPasscode(prev=>true);
    const response = await sendOTPEmailToUser(email);
    setModal({
      title: response.title,
      message: response.message,
    });
    setSendingPasscode(prev=>false);
    startOTPCountdown();
  };

  const sendEmail = async (event) => {
    event.preventDefault();
    if (showPassword) {
      return;
    }
    setIsLoading(true);
    if (email) {
      // validate email
      if (!validateEmail(email)) {
        setModal({
          title: "Error occured",
          message: "Please enter a valid email address",
        });
      } else {
        // validate user
        const response = await validateUser(email);
        if (!response) {
          setModal({
            title: "Error occured",
            message: "Could not validate user at this time. Please try again.",
          });
        } else {
          if (response.data.validUser === 1) {
            const otpSentResponse = await sendOTPEmailToUser(email);
            if (otpSentResponse.status === 0) {
              setShowPassword(true);
              startOTPCountdown();
              localStorage.setItem("pfas_otp_signin_email", email);
              if ("updateStatus" in response.data) {
                switch (response.data.updateStatus) {
                  case -1:
                    break;
                  case 0:
                    console.error(
                      "There was an error updating new PWS in your account. Please contact support."
                    );
                    break;
                  case 1:
                    //console.log('New PWS were added to your account.');
                    break;
                  case 2:
                    //console.log('No new PWS in your account.');
                    break;
                  case 3:
                    //console.log('You are not in state contact table.');
                    break;
                  default:
                    break;
                }
              }
              sessionStorage.setItem("email", email);
            } else {
              setModal({
                title: "Error occurred",
                message: otpSentResponse.message,
              });
            }
          } else {
            onLogout();
            setModal({
              title: "Error occured",
              message: "No user found with this email address",
            });
          }
        }
      }
    } else {
      setModal({
        title: "Error occured",
        message: "Please enter an email address",
      });
    }
    setIsLoading(false);
    
  };

  const sendPassword = async (event, otpArr) => {
    const otpToUse = otpArr || otp;
    event.preventDefault();
    await generateReCaptchaToken("pfas_login"); // this will create a new token in the localStorage
    setIsLoading(true);
    setIsSending(true);
    onLogin(email, otpToUse.join(""), localStorage.getItem("RECAPTCHA_TOKEN")).then(
      (response) => {
        const { isError } = response;
        if (isError) {
          const { title, message } = response;
          setModal({ title, message });
          setIsLoading(false);
          setIsSending(false);
          setOtp(["", "", "", "", "", ""]);
        } else {
          const { userInfo } = response;
          const { group: groups = [] } = userInfo;

          // making sure, we receive a valid group
          if (
            groups.length &&
            ALL_ACCESS.concat(WATER_SYSTEMS)
              .concat(VENDORS)
              .some((item) => groups.includes(item))
          ) {
            updateUserData({
              userInfo: {
                user_email: userInfo.user_email,
                first_name: userInfo.first_name,
                last_name: userInfo.last_name,
                phone_office: userInfo.phone_office,
                phone_mobile: userInfo.phone_mobile,
                organization: userInfo.organization,
                position: userInfo.position,
                language: userInfo.language,
              },
              group: groups,
              isNewUser: userInfo.is_new,
            });
            localStorage.removeItem("pfas_otp_signin_email");
            navigate("/dashboard");
          } else {
            setModal({
              title: "Error occured",
              message: "Invalid group found, contact admin for support",
            });
            onLogout();
            setIsLoading(false);
            setIsSending(false);
          }
        }
      }
    );
  };






  const handleOTPChange = (e, index) => {
    // const value = e.target.value;

    // // Allow only digits
    // if (/[^0-9]/.test(value)) return;

    // const updatedOtp = [...otp];
    // updatedOtp[index] = value;
    // setOtp(updatedOtp);

    // // Focus next input when a value is entered
    // if (value && index < otp.length - 1) {
    //   document.getElementById(`otp-input-${index + 1}`).focus();
    // }

    // // Focus previous input when backspace is pressed
    // if (!value && index > 0) {
    //   document.getElementById(`otp-input-${index - 1}`).focus();
    // }
    const value = e.target.value;

    // Allow only digits
    if (/[^0-9]/.test(value)) return;

    const updatedOtp = [...otp];
    updatedOtp[index] = value;
    setOtp(updatedOtp);

    // Focus next input when a value is entered
    if (value && index < otp.length - 1) {
      document.getElementById(`otp-input-${index + 1}`).focus();
    }
  };

  const handlePaste = (e) => {
    const pastedValue = e.clipboardData.getData("Text");

    // Only process if the pasted value is a 6-digit number
    if (/^\d{6}$/.test(pastedValue)) {
      const otpArray = pastedValue.split("");
      setOtp(pastedValue.split("").map((char) => char));
      for (let i = 0; i < 6; i++) {
        const input = document.getElementById(`otp-input-${i}`);
        if (input) input.blur();
      }
        sendPassword(e,otpArray); // You can pass any required params here
    }

    e.preventDefault(); // Prevent the default paste behavior
    
  };

  const handleSelectAll = () => {
    // Select all OTP fields for easier deletion
    // setOtp(["", "", "", "", "", ""]);
    // document.getElementById("otp-input-0").focus();
  };

  const handleBackspace = (e, index) => {
    if (e.key === "Backspace" && otp[index] === "") {
      // Move focus to previous input when backspace is pressed
      if (index > 0) {
        document.getElementById(`otp-input-${index - 1}`).focus();
      }
    }
  };

  const isOtpComplete = otp.every((digit) => digit !== "");

  return (
    <section className="w-full h-full flex justify-center items-center">
      {modal && (
        <CustomizedModal
          title={modal?.title}
          message={modal?.message}
          onCancel={() => setModal(null)}
        />
      )}
      {botModal && (
        <BotModal
          title={botModal?.title}
          message={botModal?.message}
          onCancel={() => {
            setBotModal(null);
            setShowPassword((prev) => false);
            setEmail((prev) => null);
            window.location.href = "/";
          }}
        />
      )}
      <div className="p-3 md:p-5 flex flex-col justify-center items-left max-w-[500px]">
        <img
          src="pfas-logo-hrz-color.svg"
          alt="PFAS logo vertical"
          className="h-28"
        />
        <div className="mb-8 md:p-6 flex flex-col gap-6">
          <h4 className="font-semibold text-lg md:text-xl text-primary text-center">
            Assistance Portal
          </h4>
          {showPassword ? (
            <>
             <div className="flex items-center gap-2 text-gray-500 cursor-pointer w-fit"
              onClick={(e) => backToEmail(e)}>
                <span><GoArrowLeft /></span>
                <span>Back to email</span>
              </div>
              <div className="relative">
              {/* <input
                type="text"
                className="appearance-none w-full p-3 rounded-2xl border border-black/30 focus:outline-none focus:border-accent focus:border-2"
                placeholder="One-time passcode"
                value={password}
                onChange={handlePasswordInput}
                onKeyDown={checkForEnterInPassword}
                disabled={isLoading}
              /> */}
              <div className="flex flex-col gap-2">
                <div className="flex justify-center space-x-2 mt-4">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-input-${index}`}
                  type="text"
                  maxLength="1"
                  autoComplete="off"
                  value={digit}
                  onChange={(e) => handleOTPChange(e, index)}
                  onPaste={handlePaste}
                  onClick={handleSelectAll}
                  onKeyDown={(e) => handleBackspace(e, index)}
                  className="w-12 h-12 text-xl leading-[3rem] m-0 p-0 resize-none overflow-hidden text-center text-gray-700 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary"
                />
              ))}
            </div>
            <button
                onClick={e=>sendPassword(e, null)}
                disabled={!isOtpComplete || isSending}
                className={`mt-6 px-6 py-2 bg-primary text-white font-normal text-sm rounded-md ${
                  !isOtpComplete || isSending ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {isSending ? "Verifying..." : "Verify OTP"}
              </button>
              </div>
            </div>
            </>
            
          ) : (
            <div className="relative">
              <input
                type="email"
                name="email"
                className="appearance-none w-full p-3 rounded-2xl border border-black/30 focus:outline-none focus:border-accent focus:border-2"
                placeholder="Registered Email"
                value={email}
                onChange={handleEmailInput}
                onKeyDown={checkForEnterInEmail}
                disabled={isLoading}
              />
              {isLoading ? (
                <div
                  className="absolute top-3 right-3 animate-spin w-6 h-6 border-[2px] border-current border-t-neutral text-secondary rounded-full"
                  role="status"
                  aria-label="loading"
                >
                  <span className="sr-only">Loading...</span>
                </div>
              ) : (
                <button
                  className="absolute top-0 right-3 translate-y-1/2 text-secondary"
                  onClick={(e) => sendEmail(e)}
                >
                  <FiArrowRightCircle className="w-6 h-6 hover:text-accent" />
                </button>
              )}
            </div>
          )}

          {showPassword ? (
            <>
              <p className="text-gray-500">
                If an account exists for
                <span className="text-accent"> {email}</span> then a one-time
                passcode has been sent. Enter that one-time passcode and click
                the Verify OTP button to continue.
              </p>
              <p className="text-gray-500">
                <button
                    onClick={resendOTPHandler}
                    disabled={isDisabled}
                    className="text-secondary flex items-center disabled:text-gray-500 disabled:cursor-not-allowed"
                  >
                    {
                      sendingPasscode ? (
                        <span className='text-gray-500 cursor-not-allowed'>
                        Sending...
                        </span>
                      ) :
                      (
                        <>
                        {isDisabled
                                              ? `You can request a new passcode in ${countdown} seconds`
                                              : 'Send new passcode'}
                        <span className="mt-1 ml-1">
                    <BiRightArrow />
                  </span>
                        </>
                      )
                    }
                  </button>
              </p>
              <p className="text-gray-400 text-sm">Note: Only the latest passcode is valid. If you request a new code, any previous codes will no longer work.</p>
            </>
          ) : (
            <>
              <p className="text-black/50">
                Enter you email address and click the arrow. Use the email
                address on file with the SWB (Admin, Water Quality Sampler, or
                Chief Operator). If an account with that email exists in our
                records, then a one-time passcode will be sent to that address.
              </p>
              <p className="text-black/50">
                First time signing in? Enter your email address and click the
                arrow. If your email has been vetted by the State Water Board or
                an existing user then you will be allowed to create an account.
              </p>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default SignIn;
